import superjson from "@dmca/utils/superjson";
import {
    type Dispatch,
    type SetStateAction,
    useCallback,
    useState,
    useMemo,
    useEffect,
} from "react";

export const serializer = (value: any): string => {
    return superjson.stringify(value);
};

export const deserializer = (value: string): any => {
    try {
        return superjson.parse(value);
    } catch {
        return value;
    }
};

export const stateParser = <T>(value?: T | (() => T)): T => {
    const val = typeof value === "function" ? (value as () => T)() : value;
    return val as T;
};

export function usePersistentStorage<T>(
    type: "local" | "session",
    queryKey: string,
    initialState?: T | (() => T)
): [T, Dispatch<SetStateAction<T>>] {
    if (typeof window === "undefined") {
        return [
            (typeof initialState === "function"
                ? (initialState as () => T)()
                : initialState) as T,
            () => {},
        ];
    }
    const storage = type === "local" ? localStorage : sessionStorage;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [stateInner, setStateInner] = useState<T>(() => {
        try {
            const val = storage.getItem(queryKey);
            if (val) {
                return deserializer(val);
            }
            const res = stateParser(initialState);
            return res;
        } catch {
            return stateParser(initialState);
        }
    });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        try {
            const val = storage.getItem(queryKey);
            if (val) {
                setStateInner(deserializer(val));
                return;
            }
        } catch {
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const state = useMemo<T>(() => {
        return deserializer(stateInner as string);
    }, [stateInner]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const setState = useCallback(
        (e: SetStateAction<T>) => {
            setStateInner((prevState) => {
                const next =
                    typeof e === "function"
                        ? (e as (prevState: T) => T)(prevState)
                        : e;
                try {
                    storage.setItem(queryKey, serializer(next));
                } catch {
                    // If user is in private mode or has storage restriction storage can throw.
                }
                return next;
            });
        },
        [storage, queryKey]
    );
    return [state, setState];
}
